/**
 * PunchListPdfHost — listens for the punch-list PDF events fired by
 * milestoneDocumentProviders.js's 'punchlist' provider (Construction Gates Phase D trimmings).
 * Mounted once in OutlineView, mirrors PreDesignReportHost/OpcReportHost.
 *
 *   'generate-punch-list-pdf' { projectId, msName }
 *     -> opens the compiled punch list (letterhead + issuance metadata + legal note + per-row QR
 *        into the contractor portal) in a new tab. On-demand preview only — nothing is saved.
 *   'compile-punch-list-pdf' { msId, projectId, msName }
 *     -> compiles the same PDF and freezes it into the milestone's Documents (source 'punch-list'),
 *        replacing any prior punch-list doc (generated or manually uploaded). Updates ctx.docCache
 *        directly so the Documents section reflects the new copy without a reload.
 *
 * A large punch list can take a while to compile server-side (buildPunchPdf, api/punch-items.js) —
 * both paths now surface that visibly instead of appearing to hang: preview opens its target tab
 * SYNCHRONOUSLY on click (writeGeneratingPage stamps a placeholder into it immediately, before the
 * fetch even starts) and the component itself renders a small bottom-center busy/success banner
 * (driven by `busy`/`successLabel`, previously tracked but never rendered).
 *
 * Both paths first confirm the Contractor name via a prompt (the one dialog step in this flow —
 * everything else here is fire-and-forget). The dialog is prefilled with the punch list's own
 * saved contractor_name if the team has set one, else the project's current GC contract name
 * (resolveProjectContractorName, api/punch-items.js), else blank. Whatever the user confirms is
 * persisted (setPunchContractorName) so it's remembered next time and stamped onto this render's
 * "Contractor:" line alongside "Issued: <today>" and the required legal note (both server-derived,
 * no editable-date step — see buildPunchPdf).
 */
import { useEffect, useState } from 'react'
import { getOrCreatePunchList, previewPunchListPdf, compilePunchListPdf, setPunchContractorName } from '../services/punchItems.js'
import { alertDialog, promptDialog } from './Dialog.jsx'
import { ctx } from '../outline/ctx.js'

// Writes a small "generating…" placeholder into a synchronously-opened blank tab, so there's
// something on screen immediately instead of a blank about:blank tab (or nothing at all) while
// the server compiles a punch list that can take a while with many rows/photos.
function writeGeneratingPage(tab, label) {
  try {
    tab.document.write(
      '<title>' + label + '</title><body style="margin:0;height:100vh;display:flex;' +
      'align-items:center;justify-content:center;font-family:\'DM Sans\',sans-serif;' +
      'color:#586b70;background:#f4f7f8"><p>' + label + '&hellip;</p></body>'
    )
    tab.document.close()
  } catch { /* best-effort — a failed write just leaves the tab blank */ }
}

export default function PunchListPdfHost() {
  const [busy, setBusy] = useState(false)
  const [busyLabel, setBusyLabel] = useState('')
  const [successLabel, setSuccessLabel] = useState('')

  // Auto-clear the success flash after a couple seconds — separate from the busy flow above so
  // the spinner never shows next to a "done" message.
  useEffect(() => {
    if (!successLabel) return
    const t = setTimeout(() => setSuccessLabel(''), 2200)
    return () => clearTimeout(t)
  }, [successLabel])

  useEffect(() => {
    // Returns the confirmed contractor name (persisting it if changed), or null if the user
    // cancelled — callers skip generation entirely in that case.
    async function resolveContractorName(projectId) {
      const { list, contractorName } = await getOrCreatePunchList(projectId)
      const confirmed = await promptDialog({
        title: 'Contractor',
        message: 'Contractor name for this Punch List PDF (Issued: ' + new Date().toLocaleDateString() + ').',
        defaultValue: contractorName || '',
        placeholder: 'Contractor name',
      })
      if (confirmed === null) return null
      if (confirmed !== (list.contractor_name || '')) {
        await setPunchContractorName(list.id, confirmed).catch(() => { /* best-effort — still generate with the confirmed value */ })
      }
      return confirmed
    }

    async function onPreview(e) {
      const { projectId, msName } = e.detail || {}
      if (!projectId || busy) return
      setBusy(true)
      try {
        const contractorName = await resolveContractorName(projectId)
        if (contractorName !== null) {
          setBusyLabel('Generating punch list PDF')
          // Open the tab synchronously (so the browser doesn't treat it as a blocked popup) and
          // show a placeholder immediately — compiling a long punch list (many rows/photos) can
          // take a while, and previously nothing at all happened on screen until it finished.
          const tab = window.open('', '_blank')
          if (tab) writeGeneratingPage(tab, 'Generating punch list PDF')
          try {
            await previewPunchListPdf(projectId, msName, contractorName, tab)
          } catch (err) {
            if (tab && !tab.closed) tab.close()
            throw err
          }
        }
      } catch (err) {
        await alertDialog({ title: 'Punch list PDF failed', message: (err && err.message) || 'Could not generate the PDF.' })
      }
      setBusy(false)
      setBusyLabel('')
    }
    async function onCompile(e) {
      const { msId, projectId, msName } = e.detail || {}
      if (!msId || !projectId || busy) return
      setBusy(true)
      try {
        const contractorName = await resolveContractorName(projectId)
        if (contractorName !== null) {
          setBusyLabel('Compiling & attaching punch list PDF')
          const { document } = await compilePunchListPdf(projectId, msId, msName, contractorName)
          if (!ctx.docCache) ctx.docCache = {}
          const others = (ctx.docCache[msId] || []).filter(d => d.source !== 'punch-list')
          ctx.docCache[msId] = [...others, document]
          window.dispatchEvent(new Event('outline-render'))
          setSuccessLabel('Attached to Documents')
        }
      } catch (err) {
        await alertDialog({ title: 'Punch list PDF failed', message: (err && err.message) || 'Could not compile the punch list.' })
      }
      setBusy(false)
      setBusyLabel('')
    }
    window.addEventListener('generate-punch-list-pdf', onPreview)
    window.addEventListener('compile-punch-list-pdf', onCompile)
    return () => {
      window.removeEventListener('generate-punch-list-pdf', onPreview)
      window.removeEventListener('compile-punch-list-pdf', onCompile)
    }
  }, [busy])

  // `busy` alone isn't enough to show the pill: it's set true immediately on click (to block a
  // double-fire while the Contractor promptDialog is still up) but `busyLabel` doesn't get a real
  // value until AFTER that dialog resolves — showing the pill before then flashed a bare spinner
  // with no text underneath the dialog.
  if ((!busy || !busyLabel) && !successLabel) return null
  return (
    <div style={{
      position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)', zIndex: 600,
      display: 'flex', alignItems: 'center', gap: 8, padding: '9px 18px', borderRadius: 20,
      background: '#07272D', color: '#fff', fontSize: 13, fontFamily: "'DM Sans',sans-serif",
      boxShadow: '0 4px 16px rgba(0,0,0,0.18)', pointerEvents: 'none',
    }}>
      {busy && busyLabel ? (
        <>
          <span style={{
            width: 12, height: 12, borderRadius: '50%', border: '2px solid rgba(255,255,255,0.35)',
            borderTopColor: '#fff', animation: 'aj-punch-spin 0.8s linear infinite', flexShrink: 0,
          }} />
          {busyLabel}&hellip;
          <style>{'@keyframes aj-punch-spin{to{transform:rotate(360deg)}}'}</style>
        </>
      ) : (
        <>&#10003; {successLabel}</>
      )}
    </div>
  )
}
